import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { coursesService, type ListCoursesParams } from '@/services/courses.service'
import { useAuth } from '@/hooks'
import { toast } from 'sonner'

/**
 * Hook para listar cursos com filtros
 */
export function useCourses(params?: ListCoursesParams) {
  return useQuery({
    queryKey: ['courses', params],
    queryFn: () => coursesService.listCourses(params),
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

/**
 * Hook para buscar um curso por ID
 */
export function useCourse(id: number) {
  return useQuery({
    queryKey: ['course', id],
    queryFn: () => coursesService.getCourseById(id),
    enabled: !!id,
  })
}

/**
 * Hook para buscar um curso por slug
 */
export function useCourseBySlug(slug: string) {
  return useQuery({
    queryKey: ['course', 'slug', slug],
    queryFn: () => coursesService.getCourseBySlug(slug),
    enabled: !!slug,
  })
}

/**
 * Hook para matricular em um curso
 */
export function useEnrollCourse() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (courseId: number) => {
      if (!token) throw new Error('Usuário não autenticado')
      return coursesService.enrollInCourse(courseId, token)
    },
    onSuccess: (_data, courseId) => {
      // Invalida todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['my-courses'] })
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      queryClient.invalidateQueries({ queryKey: ['enrollment', courseId] }) // ✅ Atualiza status de matrícula
      toast.success('Matrícula realizada com sucesso!')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao matricular no curso')
    },
  })
}

/**
 * Hook para cancelar matrícula
 */
export function useUnenrollCourse() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (courseId: number) => {
      if (!token) throw new Error('Usuário não autenticado')
      return coursesService.unenrollFromCourse(courseId, token)
    },
    onSuccess: (_data, courseId) => {
      // Invalida todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['my-courses'] })
      queryClient.invalidateQueries({ queryKey: ['courses'] })
      queryClient.invalidateQueries({ queryKey: ['enrollment', courseId] }) // ✅ Atualiza status de matrícula
      toast.success('Matrícula cancelada com sucesso!')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao cancelar matrícula')
    },
  })
}

/**
 * Hook para verificar se está matriculado em um curso
 */
export function useCheckEnrollment(courseId: number) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['enrollment', courseId],
    queryFn: () => {
      if (!token) return false
      return coursesService.checkEnrollment(courseId, token)
    },
    enabled: !!token && !!courseId,
  })
}

/**
 * Hook para listar cursos matriculados do usuário
 */
export function useMyEnrolledCourses() {
  const { token, isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['my-courses'],
    queryFn: async () => {
      if (!token) throw new Error('Usuário não autenticado')
      return coursesService.getMyEnrolledCourses(token)
    },
    enabled: isAuthenticated && !!token,
    retry: 1,
  })
}

/**
 * Hook para buscar o último episódio assistido (qualquer curso)
 */
export function useLastWatched() {
  const { token, isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['last-watched'],
    queryFn: () => {
      if (!token) throw new Error('Usuário não autenticado')
      return coursesService.getLastWatched(token)
    },
    enabled: isAuthenticated && !!token,
    staleTime: 1000 * 30,
  })
}

/**
 * Hook para buscar o próximo episódio a assistir
 */
export function useNextEpisode(courseId: number) {
  const { token, isAuthenticated } = useAuth()

  return useQuery({
    queryKey: ['next-episode', courseId],
    queryFn: () => {
      if (!token) throw new Error('Usuário não autenticado')
      return coursesService.getNextEpisode(courseId, token)
    },
    enabled: isAuthenticated && !!token && !!courseId,
    staleTime: 1000 * 10, // 10 segundos - atualiza rápido para refletir progresso
  })
}
